
// src/components/TrailerSelectorModal.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Ban,
  Timer,
  AlertTriangle,
  Navigation,
  MapPin,
  Calendar,
  Clock,
  Container,
  Search,
  X,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import axios from "../axiosConfig";
import truckModalHero from "../assets/truckformodal.png";
import "../styles/TruckSelectorModal.css";
import "../styles/TrailerSelectorModal.css";
import { useTranslation } from "react-i18next";

function TrailerSelectorModal({
  isOpen,
  onClose,
  onSelect,
  selectedType, // "pickup" | "delivery"   ✅ (bulk)
  selectedPickupDelivery,
  currentLoad,
  title,
  mode = "single", // "single" | "bulk"
  allPickups = [],
  allDeliveries = [],
}) {

  const quickMode = currentLoad?.isExternalCarrier === true;
  console.log("TrailerSelectorModal currentLoad:", currentLoad);
  console.log("carrierId:", currentLoad?.carrierId, "quickMode:", quickMode);
  const { t, i18n } = useTranslation([
    "trailer_selector_modal",
    "truck_selector_modal",
  ]);
  const locale = useMemo(() => {
    const lng = (i18n.language || "en").toLowerCase();
    if (lng.startsWith("pt")) return "pt-PT";
    if (lng.startsWith("es")) return "es-ES";
    if (lng.startsWith("fr")) return "fr-FR";
    return "en-GB";
  }, [i18n.language]);

  const [availableTrailers, setAvailableTrailers] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  const [loadingTrailers, setLoadingTrailers] = useState(false);
  const [manualPlate, setManualPlate] = useState("");
  const [manualPlateError, setManualPlateError] = useState("");

  const [trailerSearch, setTrailerSearch] = useState("");
  const [onlyAvailableTrailers, setOnlyAvailableTrailers] = useState(false);
  const [selectedTrailerId, setSelectedTrailerId] = useState(null);
  const [isSavingTrailer, setIsSavingTrailer] = useState(false);
  const [trailerConfirmError, setTrailerConfirmError] = useState("");

  const [freshSelected, setFreshSelected] = useState(null);
  const [freshPickups, setFreshPickups] = useState([]);
  const [freshDeliveries, setFreshDeliveries] = useState([]);

  const LOADING_STEPS = 5;
  const [prepStep, setPrepStep] = useState(0);
  const [showPrep, setShowPrep] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setSelectedTrailerId(null);
    setTrailerSearch("");
    setOnlyAvailableTrailers(false);
    setTrailerConfirmError("");
    setExpanded(new Set());
  }, [
    isOpen,
    mode,
    selectedPickupDelivery?.id,
    currentLoad?.id,
    selectedType,
  ]);

  useEffect(() => {
    if (!isOpen) return;

    let active = true;

    setFreshSelected(null);
    setFreshPickups([]);
    setFreshDeliveries([]);

    const noCache = {
      params: { _: Date.now() },
    };

    if (mode === "single" && selectedPickupDelivery?.id != null) {
      axios
        .get(
          `/pickupdeliveries/by-id/${encodeURIComponent(
            selectedPickupDelivery.id
          )}`,
          noCache
        )
        .then(({ data }) => {
          if (active) {
            setFreshSelected(data);
          }
        })
        .catch((error) => {
          console.error(
            "[TrailerSelectorModal] Error fetching selected point:",
            error
          );
        });
    } else if (mode === "bulk" && currentLoad?.id != null) {
      axios
        .get(
          `/loads/${encodeURIComponent(currentLoad.id)}/points`,
          noCache
        )
        .then(({ data }) => {
          if (active) {
            setFreshPickups(data?.pickups ?? []);
            setFreshDeliveries(data?.deliveries ?? []);
          }
        })
        .catch((error) => {
          console.error(
            "[TrailerSelectorModal] Error fetching load points:",
            error
          );
        });
    }

    return () => {
      active = false;
    };
  }, [
    isOpen,
    mode,
    selectedPickupDelivery?.id,
    currentLoad?.id,
  ]);
  
  const toggleExpand = (id, e) => {
    e?.stopPropagation?.();
    setExpanded((prev) => (prev.has(id) ? new Set() : new Set([id])));
  };
  const trPointType = (rawType) => {
    const key = `pointTypes.${String(rawType || "").toUpperCase()}`;
    const translated = t(key, { defaultValue: "" });
    return translated || rawType || "-";
  };
  const fmtDateTime = (d) => {
    try {
      return d.toLocaleString(locale, {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return d?.toString?.() ?? "";
    }
  };
  function getTrailerLabel(tr) {
    return tr?.trailerPlate || tr?.trailerNumber || tr?.plate || `#${tr?.id ?? "-"}`;
  }
  function getUniformAssignedTrailerId(points = []) {
  const ids = points
    .map((p) => p?.trailerId ?? null)
    .filter((id) => id != null);
  if (ids.length === 0) return null;
  return ids.every((id) => String(id) === String(ids[0])) ? ids[0] : null;
}
  function isTrailerBlockedForCurrent(trailer, selectedPoint) {
    if (!trailer?.sessionBlocks?.length || !selectedPoint) return null;
    if (!selectedPoint.date || !selectedPoint.hour) return null;
    const sessionId = selectedPoint.transportsessionId ?? null;
    const pointStart = new Date(
      `${selectedPoint.date}T${String(selectedPoint.hour).padStart(5, "0")}:00`
    );
    const pointEnd = new Date(pointStart.getTime() + 45 * 60 * 1000);
    for (const block of trailer.sessionBlocks) {
      const start = new Date(block.start);
      const end = new Date(block.end);
      const blockSessionId = block.transportsessionId ?? block.sessionId ?? null;
      const isSameSession =
        blockSessionId != null &&
        sessionId != null &&
        String(blockSessionId) === String(sessionId);
      // ✅ mesma regra do TruckSelectorModal: ignorar conflitos da MESMA session em single
      if (mode === "single" && isSameSession) continue;
      const overlaps = pointStart < end && start < pointEnd;
      if (overlaps) {
        return {
          isBlocked: true,
          block: {
            transportsessionId: blockSessionId,
            loadId: block.loadId ?? null,
            blockType: block.blockType,
            start,
            end,
            points: block.points || [],
          },
        };
      }
    }
    return null;
  }

  function isTrailerBlockedForBulk(trailer, allPickupsArr = [], allDeliveriesArr = []) {
  if (!trailer?.sessionBlocks?.length) return null;
  const relevantPoints = selectedType === "pickup" ? allPickupsArr : allDeliveriesArr;
  const allPoints = relevantPoints.filter((p) => p?.date && p?.hour);
  const result = [];
  for (const point of allPoints) {
    const pointStart = new Date(
      `${point.date}T${String(point.hour).padStart(5, "0")}:00`
    );
    const pointEnd = new Date(pointStart.getTime() + 45 * 60 * 1000);
    for (const block of trailer.sessionBlocks) {
      if (
        mode === "bulk" &&
        block.transportsessionId &&
        currentLoad?.transportsessionId &&
        String(block.transportsessionId) === String(currentLoad.transportsessionId)
      ) {
        continue;
      }
      const start = new Date(block.start);
      const end = new Date(block.end);
      const isOverlap = pointStart < end && start < pointEnd;
      if (isOverlap) {
        const existing = result.find(
          (b) =>
            b.blockType === block.blockType &&
            (block.blockType === "LOAD_FULL"
              ? b.loadId === block.loadId
              : b.sessionId === block.transportsessionId)
        );
        const cause = {
          type: point.type,
          location: point.location,
          date: point.date,
          hour: point.hour,
        };
        if (existing) {
          existing.causedBy.push(cause);
          existing.causedBy.sort(
            (a, b) =>
              new Date(`${a.date}T${a.hour}:00`) - new Date(`${b.date}T${b.hour}:00`)
          );
        } else {
          result.push({
            sessionId: block.transportsessionId,
            loadId: block.loadId ?? null,
            blockType: block.blockType,
            start,
            end,
            points: block.points || [],
            causedBy: [cause],
          });
        }
      }
    }
  }
  if (result.length === 0) return null;
  const seen = new Set();
  const dedup = [];
  for (const block of result) {
    let key;
    if (block.blockType === "FULL_SESSION") key = `FULL_SESSION-${block.sessionId}`;
    else if (block.blockType === "LOAD_FULL" || block.blockType === "LOAD_HALF")
      key = `${block.blockType}-${block.loadId}`;
    else if (block.blockType === "POINT")
      key = `POINT-${block.loadId}-${block.start.toISOString()}`;
    if (!seen.has(key)) {
      seen.add(key);
      dedup.push(block);
    }
  }
  const hasFullSession = dedup.some((b) => b.blockType === "FULL_SESSION");
  const hasFullLoad = dedup.some((b) => b.blockType === "LOAD_FULL");
  let filtered = dedup;
  if (hasFullSession) {
    filtered = dedup.filter((b) => b.blockType === "FULL_SESSION");
  } else if (hasFullLoad) {
    filtered = dedup.filter(
      (b) => b.blockType === "LOAD_FULL" || b.blockType === "LOAD_HALF"
    );
  }
  return { isBlocked: filtered.length > 0, blocks: filtered };
}


  useEffect(() => {
    if (!isOpen) return;
    setExpanded(new Set());
    // ✅ QUICK MODE: nada de fetch/blocking (igual truck/driver)
    if (quickMode) {
      setManualPlate("");
      setManualPlateError("");
      setAvailableTrailers([]);
      setLoadingTrailers(false);
      setShowPrep(false);
      setPrepStep(0);
      return;
    }
    const sid =
      mode === "single"
        ? selectedPickupDelivery?.transportsessionId ?? currentLoad?.transportsessionId ?? null
        : currentLoad?.transportsessionId ?? null;
    const noCache = { params: { _: Date.now() } };
    // ✅ relax blocks só em SINGLE (como truck/driver)
    const blockingParams =
      sid && mode === "single"
        ? { params: { _: Date.now(), originSessionId: sid } }
        : noCache;
    setLoadingTrailers(true);
    Promise.all([
      axios.get("/pickupdeliveries/trailers/with-latest-assignments", noCache),
      axios.get("/pickupdeliveries/trailer-blocking", blockingParams),
    ])
      .then(([trailersRes, blocksRes]) => {
        const blockingData = blocksRes.data || [];
        const trailersWithBlocking = (trailersRes.data || []).map((tr) => {
          const blockEntry = blockingData.find((b) => b.trailerId === tr.id);
          return { ...tr, sessionBlocks: blockEntry?.sessionBlocks || [] };
        });
        setAvailableTrailers(trailersWithBlocking);
      })
      .catch((err) => console.error("Error fetching trailer data", err))
      .finally(() => setLoadingTrailers(false));
  }, [
    isOpen,
    quickMode,
    selectedPickupDelivery?.transportsessionId,
    currentLoad?.transportsessionId,
    mode,
  ]);
  // ===== Fun loader logic (igual ao truck) =====
  useEffect(() => {
    if (!isOpen) return;
    let tickTimer = null;
    let doneTimer = null;
    if (loadingTrailers) {
      setShowPrep(true);
      setPrepStep(0);
      // 0->3 rápido, segura no 3 até acabar fetch
      let s = 0;
      tickTimer = setInterval(() => {
        s += 1;
        setPrepStep((prev) => Math.min(3, Math.max(prev, s)));
        if (s >= 3 && tickTimer) {
          clearInterval(tickTimer);
          tickTimer = null;
        }
      }, 350);
    } else {
      if (showPrep) {
        setPrepStep(4);
        doneTimer = setTimeout(() => {
          setShowPrep(false);
          setPrepStep(0);
        }, 650);
      }
    }
    return () => {
      if (tickTimer) clearInterval(tickTimer);
      if (doneTimer) clearTimeout(doneTimer);
    };
  }, [isOpen, loadingTrailers, showPrep]);
  if (!isOpen) return null;
  // --- title ---
  const providedTitle = (title ?? "").trim();
  const computedTitle = providedTitle || t("titles.trailer", { defaultValue: "Assign trailer" });
const pointType = String(
  mode === "single"
    ? selectedPickupDelivery?.type || selectedType || ""
    : selectedType || ""
).toUpperCase();

const assignmentSide =
  pointType === "PICKUP"
    ? "pickup"
    : pointType === "DELIVERY"
      ? "delivery"
      : null;

const assignmentLoadId =
  mode === "single"
    ? selectedPickupDelivery?.loadId ?? currentLoad?.id ?? null
    : currentLoad?.id ?? null;

// Identificar a atribuição real da carga a partir da resposta
// /pickupdeliveries/trailers/with-latest-assignments.
const assignedFromServer =
  assignmentSide && assignmentLoadId != null
    ? availableTrailers.find((trailer) =>
        Array.isArray(trailer.assignments) &&
        trailer.assignments.some((assignment) => {
          const sameLoad =
            String(assignment.loadId) ===
            String(assignmentLoadId);

          const sameSide =
            assignment.type === "full" ||
            String(assignment.side || "").toLowerCase() ===
              assignmentSide;

          return sameLoad && sameSide;
        })
      )?.id ?? null
    : null;

// Para pontos estratégicos, respeitar arrival/departure.
const pointAssignedTrailerId =
  pointType === "STRATEGICPOINT"
    ? selectedPickupDelivery?.trailerRole === "departure"
      ? selectedPickupDelivery?.trailerIdDeparture ?? null
      : selectedPickupDelivery?.trailerIdArrival ?? null
    : selectedPickupDelivery?.trailerId ?? null;

// A carga também contém a atribuição consolidada.
const loadAssignedTrailerId =
  assignmentSide === "pickup"
    ? currentLoad?.trailerIdPickup ?? null
    : assignmentSide === "delivery"
      ? currentLoad?.trailerIdDelivery ?? null
      : null;

const assignedTrailerId =
  assignedFromServer ??
  (mode === "single"
    ? pointAssignedTrailerId ?? loadAssignedTrailerId
    : loadAssignedTrailerId ??
      getUniformAssignedTrailerId(
        assignmentSide === "pickup"
          ? allPickups
          : allDeliveries
      ));

// Fallback para situações em que existe uma matrícula
// guardada, mas o identificador ainda não está disponível.
const assignedTrailerPlate =
  pointType === "STRATEGICPOINT"
    ? selectedPickupDelivery?.trailerRole === "departure"
      ? selectedPickupDelivery?.trailerDeparturePlate ?? null
      : selectedPickupDelivery?.trailerArrivalPlate ?? null
    : selectedPickupDelivery?.trailerPlate ??
      (assignmentSide === "pickup"
        ? currentLoad?.trailerPickupInfo
        : assignmentSide === "delivery"
          ? currentLoad?.trailerDeliveryInfo
          : null);

const normalizeTrailerPlate = (value) =>
  String(value ?? "")
    .trim()
    .toUpperCase();

const isCurrentTrailer = (trailer) => {
  if (assignedTrailerId != null) {
    return String(trailer.id) === String(assignedTrailerId);
  }

  const plate = normalizeTrailerPlate(assignedTrailerPlate);

  return (
    plate !== "" &&
    normalizeTrailerPlate(getTrailerLabel(trailer)) === plate
  );
};

const sorted = [...availableTrailers].sort((a, b) => {
  const aAssigned = isCurrentTrailer(a);
  const bAssigned = isCurrentTrailer(b);

  if (aAssigned !== bAssigned) {
    return aAssigned ? -1 : 1;
  }

  const aConflict =
    mode === "bulk"
      ? isTrailerBlockedForBulk(a, allPickups, allDeliveries)
      : isTrailerBlockedForCurrent(a, selectedPickupDelivery);

  const bConflict =
    mode === "bulk"
      ? isTrailerBlockedForBulk(b, allPickups, allDeliveries)
      : isTrailerBlockedForCurrent(b, selectedPickupDelivery);

  const aBlocked = !!aConflict?.isBlocked;
  const bBlocked = !!bConflict?.isBlocked;

  if (aBlocked !== bBlocked) {
    return aBlocked ? 1 : -1;
  }

  return getTrailerLabel(a).localeCompare(getTrailerLabel(b));
});

const trailerConflict = (trailer) =>
  mode === "bulk"
    ? isTrailerBlockedForBulk(trailer, allPickups, allDeliveries)
    : isTrailerBlockedForCurrent(trailer, selectedPickupDelivery);

const isTrailerUnavailable = (trailer) =>
  isCurrentTrailer(trailer) ||
  !!trailerConflict(trailer)?.isBlocked;

const selectableTrailerCount = availableTrailers.filter(
  (trailer) => !isTrailerUnavailable(trailer)
).length;

const gridTrailers = sorted.filter((trailer) => {
  if (
    onlyAvailableTrailers &&
    isTrailerUnavailable(trailer) &&
    !isCurrentTrailer(trailer)
  ) {
    return false;
  }

  const query = trailerSearch.trim().toLocaleLowerCase();

  if (!query) return true;

  return [
    getTrailerLabel(trailer),
    trailer.type,
    trailer.currentLocation,
    trailer.location,
  ]
    .filter(Boolean)
    .join(" ")
    .toLocaleLowerCase()
    .includes(query);
});

const selectedTrailer = availableTrailers.find(
  (trailer) =>
    selectedTrailerId != null &&
    String(trailer.id) === String(selectedTrailerId)
);

const mergeContextPoint = (base, fresh) => {
  if (!fresh) return base;
  if (!base) return fresh;

  const pick = (newValue, oldValue) =>
    newValue !== undefined &&
    newValue !== null &&
    newValue !== ""
      ? newValue
      : oldValue;

  return {
    ...base,
    ...fresh,
    id: pick(fresh.id, base.id),
    type: pick(fresh.type, base.type),
    location: pick(fresh.location, base.location),
    city: pick(fresh.city, base.city),
    country: pick(fresh.country, base.country),
    date: pick(fresh.date, base.date),
    hour: pick(fresh.hour, base.hour),
    hourEnd: pick(fresh.hourEnd, base.hourEnd),
    scheduleType: pick(fresh.scheduleType, base.scheduleType),
  };
};

const effectivePickups =
  freshPickups.length > 0 ? freshPickups : allPickups;

const effectiveDeliveries =
  freshDeliveries.length > 0 ? freshDeliveries : allDeliveries;

const contextPoint =
  mode === "single"
    ? mergeContextPoint(
        selectedPickupDelivery,
        freshSelected
      )
    : selectedType === "pickup"
      ? mergeContextPoint(
          allPickups[0],
          effectivePickups[0]
        )
      : mergeContextPoint(
          allDeliveries[0],
          effectiveDeliveries[0]
        );

const contextType = String(
  contextPoint?.type || selectedType || ""
).toUpperCase();

const contextLabel =
  contextType === "PICKUP"
    ? t("pointTypes.PICKUP", {
        ns: "truck_selector_modal",
        defaultValue: "Pick-up",
      })
    : contextType === "DELIVERY"
      ? t("pointTypes.DELIVERY", {
          ns: "truck_selector_modal",
          defaultValue: "Delivery",
        })
      : t("titles.single.strategicPoint", {
          ns: "truck_selector_modal",
          defaultValue: "Strategic point",
        });

const companyLoadNumber =
  currentLoad?.loadNumber ??
  contextPoint?.load?.loadNumber ??
  null;

const displayDate = (raw) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(
    String(raw ?? "")
  );

  return match
    ? `${match[3]}/${match[2]}/${match[1]}`
    : "—";
};

const displayTime = (point) => {
  const start = String(point?.hour || "").slice(0, 5);
  const end = String(point?.hourEnd || "").slice(0, 5);

  return (
    String(point?.scheduleType || "").toUpperCase() === "WINDOW" &&
    start &&
    end
  )
    ? `${start}–${end}`
    : start || "—";
};

const getTrailerTypeLabel = (trailer) => {
  const value = trailer?.type;

  return typeof value === "string" && value.trim()
    ? value.trim()
    : "Tipo não definido";
};

const confirmTrailerSelection = async () => {
  if (
    !selectedTrailer ||
    isSavingTrailer ||
    loadingTrailers ||
    showPrep ||
    isTrailerUnavailable(selectedTrailer)
  ) {
    return;
  }

  setIsSavingTrailer(true);
  setTrailerConfirmError("");

  try {
    if (typeof onSelect !== "function") {
      throw new Error("A função de atribuição não está disponível.");
    }

    const result = await onSelect(selectedTrailer);

    if (result === false) {
      throw new Error("A atribuição não foi confirmada.");
    }

    onClose?.();
  } catch (error) {
    console.error("[TrailerSelectorModal] Assignment failed:", error);

    setTrailerConfirmError(
      error?.message || "Não foi possível atribuir o reboque."
    );
  } finally {
    setIsSavingTrailer(false);
  }
};
  const renderBlockTitle = (block) => {
    if (!block) return "-";
    if (block.blockType === "LOAD_FULL") {
      return t("blocking.loadFull", { loadId: block.loadId ?? "?" });
    }
    if (block.blockType === "LOAD_HALF") {
      return t("blocking.loadHalf", { loadId: block.loadId ?? "?", scope: "" });
    }
    if (block.blockType === "POINT") {
      const p0 = block.points?.[0];
      return t("blocking.singleAssignment", {
        type: trPointType(p0?.type ?? "?"),
        location: p0?.location ?? "?",
      });
    }
    return t("blocking.session", { sessionId: block.transportsessionId ?? "?" });
  };

  

  const handleQuickAssign = async () => {
  const plate = String(manualPlate || "").trim().toUpperCase();

  if (!plate) {
    setManualPlateError(
      t("quickMode.errors.required", {
        defaultValue: "❌ Trailer plate is required.",
      })
    );
    return;
  }

  setManualPlateError("");

  try {
    // ✅ Delegate to parent, exactly like clicking a normal trailer card.
    // Parent already knows how to:
    // - update optimistic UI
    // - hit the correct backend endpoint
    // - refresh locations / loads
    onSelect?.({
      id: null,
      trailerPlate: plate,
      manualPlate: plate,
      isManual: true,
    });

    onClose?.();
  } catch (e) {
    console.error("❌ Quick trailer assign failed:", e);
    setManualPlateError(
      t("quickMode.errors.failed", {
        defaultValue: "❌ Failed to assign this trailer plate.",
      })
    );
  }
};




  return (
    <div
      className="truck-modal-overlay trailer-modal-overlay"
      onMouseDown={(event) => {
        if (
          event.target === event.currentTarget &&
          !isSavingTrailer
        ) {
          onClose?.();
        }
      }}
    >
      <div className="truck-modal tsm-premium trailer-premium-modal">

        <aside className="tsm-premium-side">

          <div className="tsm-premium-side-intro">
            <span className="tsm-premium-side-icon">
              <img
                src="/icons/trailer-optimized.webp"
                width={34}
                height={25}
                alt=""
                aria-hidden="true"
                draggable={false}
              />
            </span>

            <span>ASSIGN TRAILER TO</span>
          </div>

          <h2 className="tsm-premium-point-title">
            {mode === "bulk"
              ? selectedType === "pickup"
                ? "ALL PICK-UPS"
                : "ALL DELIVERIES"
              : `${contextLabel}${
                  contextPoint?.id != null
                    ? ` #${contextPoint.id}`
                    : ""
                }`}
          </h2>

          {companyLoadNumber != null &&
            String(companyLoadNumber).trim() !== "" && (
              <span className="tsm-premium-load-tag">
                LOAD #{companyLoadNumber}
              </span>
            )}

          <div
            className="tsm-premium-hero"
            style={{
              backgroundImage: `linear-gradient(
                180deg,
                rgba(8, 25, 46, 0.04) 0%,
                rgba(8, 25, 46, 0.13) 34%,
                rgba(8, 25, 46, 0.50) 74%,
                #071a2e 100%
              ), url("${truckModalHero}")`,
              backgroundSize: "cover, cover",
              backgroundPosition: "center, 59% center",
              backgroundRepeat: "no-repeat",
              opacity: 1,
            }}
            aria-hidden="true"
          />

          <div className="tsm-premium-context">

            <div className="tsm-premium-context-line">
              <MapPin size={19} />

              <div>
                <small>Location</small>

                <strong>
                  {contextPoint?.location || "—"}
                </strong>

                <span>
                  {[
                    contextPoint?.city ??
                      contextPoint?.place?.city,
                    contextPoint?.country ??
                      contextPoint?.place?.country,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </div>
            </div>

            <div className="tsm-premium-context-line">
              <Calendar size={19} />

              <div>
                <small>Date</small>

                <strong>
                  {displayDate(contextPoint?.date)}
                </strong>
              </div>
            </div>

            <div className="tsm-premium-context-line">
              <Clock size={19} />

              <div>
                <small>Schedule</small>

                <strong>
                  {displayTime(contextPoint)}
                </strong>
              </div>
            </div>

          </div>

          <div className="tsm-premium-route-card">
            <svg
              viewBox="0 0 310 44"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <path
                d="M12 31 C84 -6 139 64 226 22 S284 23 299 15"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeDasharray="5 5"
              />

              <circle
                cx="12"
                cy="31"
                r="5"
                fill="currentColor"
              />

              <circle
                cx="299"
                cy="15"
                r="6"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              />
            </svg>

            <div className="tsm-premium-route-summary">
              <strong>
                {contextPoint?.location || "—"}
              </strong>

              <span>
                {mode === "bulk"
                  ? `${
                      selectedType === "pickup"
                        ? effectivePickups.length
                        : effectiveDeliveries.length
                    } stops`
                  : contextLabel}
              </span>
            </div>
          </div>

        </aside>

        <section className="tsm-premium-main">
          <header className="tsm-premium-heading">
            <div>
              <h2>
                {quickMode
                  ? "Quick trailer assignment"
                  : "Select an Available Trailer"}
              </h2>

              <p>
                Choose a trailer for this transport assignment.
              </p>
            </div>

            <button
              type="button"
              className="tsm-premium-close-x"
              onClick={onClose}
              disabled={isSavingTrailer}
              aria-label="Close"
            >
              <X size={21} />
            </button>
          </header>

          {!quickMode && showPrep && (
            <div className="tsm-prepbar" role="status">
              <div className="tsm-prep-top">
                <span className={`tsm-prep-chip ${prepStep >= 4 ? "done" : ""}`}>
                  {prepStep >= 4 ? "Frota pronta" : "A preparar frota"}
                </span>

                <div className="tsm-prep-titlewrap">
                  <div className="tsm-prep-title">
                    {prepStep >= 4
                      ? "RESULTADOS PRONTOS"
                      : "A ORDENAR RESULTADOS"}
                  </div>

                  <div className="tsm-prep-subtitle">
                    A verificar a disponibilidade dos reboques.
                  </div>
                </div>
              </div>

              <div
                className="tsm-prep-track"
                style={{
                  "--pos": (prepStep / (LOADING_STEPS - 1)) * 100,
                }}
              >
                <div className="tsm-prep-line" />

                <div className="tsm-prep-truck" aria-hidden="true">
                  <img
                    src="/icons/trailer-optimized.webp"
                    width={21}
                    height={15}
                    alt=""
                  />
                </div>

                {Array.from({ length: LOADING_STEPS }).map((_, index) => (
                  <div
                    key={index}
                    style={{
                      "--x": (index / (LOADING_STEPS - 1)) * 100,
                    }}
                    className={[
                      "tsm-prep-node",
                      index < prepStep ? "done" : "",
                      index === prepStep ? "active" : "",
                    ].filter(Boolean).join(" ")}
                  />
                ))}
              </div>
            </div>
          )}

          {quickMode ? (
            <>
              <div className="truck-quick-box">
                <div className="trailer-quick-head">
                  <div className="trailer-quick-head-left">
                    <Container size={22} />

                    <div className="trailer-quick-head-texts">
                      <div className="trailer-quick-head-title">
                        Manual trailer assignment
                      </div>
                      <div className="trailer-quick-head-sub">
                        Enter the trailer plate provided by the carrier.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="trailer-quick-row">
                  <input
                    className={`trailer-quick-input ${
                      manualPlateError ? "input-error" : ""
                    }`}
                    value={manualPlate}
                    onChange={(event) => {
                      setManualPlate(event.target.value);
                      setManualPlateError("");
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") handleQuickAssign();
                    }}
                    placeholder="Trailer plate"
                  />
                </div>

                {manualPlateError && (
                  <div className="trailer-quick-error">
                    {manualPlateError}
                  </div>
                )}
              </div>

              <div className="tsm-premium-footer">
                <div className="tsm-premium-footer-buttons">
                  <button
                    type="button"
                    className="truck-modal-btn truck-modal-btn--secondary"
                    onClick={onClose}
                  >
                    Fechar
                  </button>

                  <button
                    type="button"
                    className="truck-modal-btn truck-modal-btn--primary"
                    onClick={handleQuickAssign}
                  >
                    Assign trailer
                    <ArrowRight size={17} />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="tsm-premium-toolbar">
                <label className="tsm-premium-search">
                  <Search size={18} />

                  <input
                    type="search"
                    value={trailerSearch}
                    onChange={(event) =>
                      setTrailerSearch(event.target.value)
                    }
                    placeholder="Search by plate or trailer type..."
                  />
                </label>

                <span className="tsm-premium-available-count">
                  {loadingTrailers || showPrep
                    ? "Checking..."
                    : `${selectableTrailerCount} available`}
                </span>

                <div className="tsm-premium-filters">
                  <button
                    type="button"
                    className={!onlyAvailableTrailers ? "is-active" : ""}
                    aria-pressed={!onlyAvailableTrailers}
                    onClick={() => setOnlyAvailableTrailers(false)}
                  >
                    All
                  </button>

                  <button
                    type="button"
                    className={onlyAvailableTrailers ? "is-active" : ""}
                    aria-pressed={onlyAvailableTrailers}
                    onClick={() => setOnlyAvailableTrailers(true)}
                  >
                    Available
                  </button>
                </div>
              </div>

              <div
                className="tsm-premium-columns"
                aria-hidden="true"
              >
                <span>TRAILER</span>
                <span>LOCATION</span>
                <span>STATUS</span>
                <span>SELECT</span>
              </div>

              <div className="truck-grid">
                {gridTrailers.map((trailer) => {
                  const conflict = trailerConflict(trailer);
                  const alreadyAssigned = isCurrentTrailer(trailer);
                  const hasConflict = !!conflict?.isBlocked;
                  const isExpanded = expanded.has(trailer.id);

                  const isSelected =
                    !alreadyAssigned &&
                    !hasConflict &&
                    selectedTrailerId != null &&
                    String(selectedTrailerId) === String(trailer.id);

                  const cannotInteract =
                    isSavingTrailer || loadingTrailers || showPrep;

                  const statusText = alreadyAssigned
                    ? "Já atribuído"
                    : isSelected
                      ? "Selecionado"
                      : hasConflict
                        ? "Indisponível"
                        : "Disponível";

                  const handleTrailerClick = () => {
                    if (cannotInteract) return;

                    if (hasConflict && !alreadyAssigned) {
                      toggleExpand(trailer.id);
                      return;
                    }

                    if (alreadyAssigned) return;

                    setSelectedTrailerId((previous) =>
                      String(previous) === String(trailer.id)
                        ? null
                        : trailer.id
                    );

                    setTrailerConfirmError("");
                  };

                  const trailerLocation =
                    typeof trailer.currentLocation === "string" &&
                    trailer.currentLocation.trim()
                      ? trailer.currentLocation
                      : typeof trailer.location === "string" &&
                        trailer.location.trim()
                        ? trailer.location
                        : "—";

                  return (
                    <div className="truck-grid-item" key={trailer.id}>
                      <div
                        className={[
                          "truck-card",
                          hasConflict && !alreadyAssigned ? "blocked" : "",
                          alreadyAssigned ? "tsm-is-assigned" : "",
                          isSelected ? "tsm-is-selected" : "",
                        ].filter(Boolean).join(" ")}
                        role="button"
                        tabIndex={
                          cannotInteract || alreadyAssigned ? -1 : 0
                        }
                        aria-disabled={cannotInteract || alreadyAssigned}
                        aria-pressed={
                          !hasConflict && !alreadyAssigned
                            ? isSelected
                            : undefined
                        }
                        aria-expanded={
                          hasConflict && !alreadyAssigned
                            ? isExpanded
                            : undefined
                        }
                        onClick={handleTrailerClick}
                        onKeyDown={(event) => {
                          if (event.target !== event.currentTarget) return;

                          if (
                            event.key === "Enter" ||
                            event.key === " "
                          ) {
                            event.preventDefault();
                            handleTrailerClick();
                          }
                        }}
                      >
                        <div className="truck-card-top">
                          <div className="truck-left">
                            <span className="truck-icon">
                              <img
                                src="/icons/trailer-optimized.webp"
                                width={20}
                                height={15}
                                alt=""
                                aria-hidden="true"
                                draggable={false}
                                loading="eager"
                                decoding="async"
                              />
                            </span>

                            <span className="tsm-premium-truck-identity">
                              <strong className="truck-plate">
                                {getTrailerLabel(trailer)}
                              </strong>

                              <small className="tsm-premium-truck-subtitle">
                                {getTrailerTypeLabel(trailer)}
                              </small>
                            </span>
                          </div>
                        </div>

                        <div className="tsm-premium-location">
                          <MapPin size={15} aria-hidden="true" />
                          <span>{trailerLocation}</span>
                        </div>

                        <div
                          className={[
                            "truck-status",
                            alreadyAssigned
                              ? "assigned"
                              : isSelected
                                ? "selected"
                                : hasConflict
                                  ? "blocked"
                                  : "ok",
                          ].join(" ")}
                        >
                          {statusText}
                        </div>

                        <span
                          className="tsm-premium-choice"
                          aria-hidden="true"
                        >
                          {alreadyAssigned || isSelected ? (
                            <CheckCircle2 size={25} />
                          ) : (
                            <span />
                          )}
                        </span>

                        {hasConflict && !alreadyAssigned && isExpanded && (
                          <div className="block-details tsm-conflict-details show">
                            <div className="tsm-conflict-heading">
                              <span className="tsm-conflict-heading-icon">
                                <AlertTriangle size={21} />
                              </span>

                              <div className="tsm-conflict-heading-text">
                                <strong>Conflito de disponibilidade</strong>
                                <small>
                                  Este reboque está ocupado neste período.
                                </small>
                              </div>

                              <span className="tsm-conflict-badge">
                                Indisponível
                              </span>
                            </div>

                            {(conflict?.blocks ||
                              (conflict?.block ? [conflict.block] : [])
                            ).map((block, index) => (
                              <div className="tsm-conflict-block" key={index}>
                                <div className="tsm-conflict-reason">
                                  <Ban size={16} />
                                  <strong>{renderBlockTitle(block)}</strong>
                                </div>

                                {block?.start && block?.end && (
                                  <div className="blocked-interval">
                                    <Timer size={16} />
                                    <span>
                                      {fmtDateTime(new Date(block.start))}
                                      {" → "}
                                      {fmtDateTime(new Date(block.end))}
                                    </span>
                                  </div>
                                )}

                                {block?.points?.length > 0 && (
                                  <ul className="blocked-points-list">
                                    {block.points.map((point, pointIndex) => (
                                      <li key={pointIndex}>
                                        {trPointType(point.type)}
                                        {" — "}
                                        {point.location || "—"}
                                        {" · "}
                                        {point.date || "—"} {point.hour || ""}
                                      </li>
                                    ))}
                                  </ul>
                                )}

                                {block?.causedBy?.map((cause, causeIndex) => (
                                  <div
                                    className="tsm-conflict-cause"
                                    key={causeIndex}
                                  >
                                    <AlertTriangle size={15} />
                                    <span>
                                      {trPointType(cause.type)}
                                      {" — "}
                                      {cause.location || "—"}
                                      {" · "}
                                      {cause.date || "—"} {cause.hour || ""}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="tsm-premium-footer">
                {trailerConfirmError && (
                  <small
                    className="tsm-premium-error"
                    role="alert"
                  >
                    {trailerConfirmError}
                  </small>
                )}

                <div className="tsm-premium-footer-buttons">
                  <button
                    type="button"
                    className="truck-modal-btn truck-modal-btn--secondary"
                    onClick={onClose}
                    disabled={isSavingTrailer}
                  >
                    Fechar
                  </button>

                  <button
                    type="button"
                    className="truck-modal-btn truck-modal-btn--primary"
                    onClick={confirmTrailerSelection}
                    disabled={
                      !selectedTrailer ||
                      isSavingTrailer ||
                      loadingTrailers ||
                      showPrep ||
                      isTrailerUnavailable(selectedTrailer)
                    }
                  >
                    {isSavingTrailer
                      ? "Selecting..."
                      : "Select trailer"}

                    <ArrowRight size={17} />
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );

}
export default TrailerSelectorModal;
